/* Mike's "Pairs well with" rows — quantity stepper + add to cart.
 *
 * Deliberately NOT <product-form-component>: inside the product section that component listens
 * to the MAIN product's variant events, gets stuck "in progress" and queues adds forever
 * (assets/product-form.js:208/286/303). Same pattern as assets/keg-accessories.js instead:
 * one POST /cart/add.js (the site-wide deposit wrapper in mikes-deposits.js adds any deposit
 * to that same request), then a cart:update carrying a real itemCount so Horizon's drawer and
 * header bubble refresh. Design: shopify-dev/pairs-well-with/DESIGN.md
 *
 * Invariants (Codex final review, 2026-10-05):
 *  - one add at a time for the whole page, and nothing but the add's own settle releases it
 *    (a 15 s timeout guarantees it settles; pageshow never releases it);
 *  - the row's quantity is frozen from the moment the request is sent;
 *  - "Added ✓" only when Shopify answers with a JSON line-item list; anything else (a bot-challenge
 *    page, a timeout, a dropped connection) is "unconfirmed": re-read the cart, resync deposits, ask
 *    the shopper to check the cart before adding again.
 */
(function () {
  if (window.__mikesPairsWith) return;
  window.__mikesPairsWith = true;
  var TIMEOUT_MS = 15000;
  var busy = false;

  function readCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  function dispatchCart(cart) {
    var data = { source: 'mikes-pairs-with', sections: {} };
    if (cart && typeof cart.item_count === 'number') data.itemCount = cart.item_count;
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { sourceId: 'mikes-pairs-with', data: data } }));
    document.dispatchEvent(new CustomEvent('cart-update', { bubbles: true }));
  }
  function reconcile() {                 // after any uncertain or failed add: show the real cart, fix deposits now
    readCart().then(function (cart) { if (cart) dispatchCart(cart); });
    if (typeof window.mikesDepositsResync === 'function') window.mikesDepositsResync();
  }
  function qtyOf(row) {
    var i = row.querySelector('[data-mpw-qty]');
    var n = i ? parseInt(i.value, 10) : 1;
    return isNaN(n) || n < 1 ? 1 : Math.min(n, 99);
  }
  function money(cents, root) {
    try {
      return (cents / 100).toLocaleString(document.documentElement.lang || undefined,
        { style: 'currency', currency: root.dataset.currency || 'USD' });
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function syncRow(row) {               // the one place the shown price is derived from the input
    var input = row.querySelector('[data-mpw-qty]');
    var price = row.querySelector('[data-mpw-price]');
    if (!input || !price) return;
    var q = qtyOf(row);
    input.value = q;
    price.textContent = money(parseInt(price.dataset.unitCents, 10) * q, row.closest('[data-mpw]'));
  }
  function setControls(row, enabled) {
    row.querySelectorAll('[data-mpw-step], [data-mpw-qty]').forEach(function (el) { el.disabled = !enabled; });
  }
  function syncAll() {
    document.querySelectorAll('[data-mpw-row]').forEach(syncRow);
    if (busy) return;                   // an add in flight settles itself (timeout guarantees it); never release it here
    document.querySelectorAll('[data-mpw]').forEach(function (root) {
      root.querySelectorAll('[data-mpw-row]').forEach(function (row) {
        var b = row.querySelector('[data-mpw-add]');
        if (!b || b.classList.contains('is-added')) return;
        b.disabled = false; b.textContent = root.dataset.tAdd;
        setControls(row, true);
      });
    });
  }

  document.addEventListener('click', function (e) {
    var step = e.target.closest('[data-mpw-step]');
    if (step) {
      var srow = step.closest('[data-mpw-row]');
      var input = srow.querySelector('[data-mpw-qty]');
      if (input.disabled || step.disabled) return;
      input.value = Math.max(1, Math.min(99, qtyOf(srow) + parseInt(step.getAttribute('data-mpw-step'), 10)));
      syncRow(srow);
      return;
    }
    var btn = e.target.closest('[data-mpw-add]');
    if (!btn) return;
    e.preventDefault();
    if (busy || btn.disabled) return;
    var row = btn.closest('[data-mpw-row]');
    var root = row.closest('[data-mpw]');
    var err = row.querySelector('[data-mpw-error]');
    var live = root.querySelector('[data-mpw-live]');
    var qty = qtyOf(row);
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS) : null;
    busy = true;
    btn.disabled = true;
    btn.textContent = root.dataset.tAdding;
    setControls(row, false);            // freeze the quantity that is being sent
    err.hidden = true;
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(btn.dataset.variantId), quantity: qty }] }),
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (r) {
        return r.text().then(function (text) {
          var body = null;
          try { body = JSON.parse(text); } catch (x) { /* not JSON: a challenge page or a proxy error */ }
          if (!r.ok) {
            var rejected = new Error((body && (body.description || body.message)) || root.dataset.tError);
            rejected.kind = 'rejected';
            throw rejected;
          }
          if (!body || !Array.isArray(body.items)) {
            var odd = new Error(root.dataset.tUnconfirmed);
            odd.kind = 'unconfirmed';
            throw odd;
          }
          return body;
        });
      })
      .then(readCart)
      .then(function (cart) {
        dispatchCart(cart);
        btn.textContent = root.dataset.tAdded;
        btn.classList.add('is-added');
        if (live) live.textContent = root.dataset.tAdded;
        // controls stay frozen: the row now shows exactly what was added
      })
      .catch(function (e2) {
        btn.disabled = false;
        btn.textContent = root.dataset.tAdd;
        setControls(row, true);
        err.textContent = e2 && e2.kind === 'rejected' ? e2.message : root.dataset.tUnconfirmed;
        err.hidden = false;
        reconcile();                    // rejected adds can be partial; unconfirmed ones may have landed
      })
      .then(function () { if (timer) clearTimeout(timer); busy = false; });
  });

  document.addEventListener('input', function (e) {
    if (e.target.matches && e.target.matches('[data-mpw-qty]')) syncRow(e.target.closest('[data-mpw-row]'));
  });
  // Browsers restore input values after scripts run (and on Back) without change events:
  // re-derive prices on load and on EVERY pageshow (company-brain rederive-ui-from-inputs-on-every-pageshow).
  window.addEventListener('load', syncAll);
  window.addEventListener('pageshow', syncAll);
})();
